<template>
  <div class="bank-page">
    <title-bar :title="$lang.bank_title || 'Bank Card'" />

    <div v-if="bankReadError" class="bank-diagnostic">
      {{ bankReadError }}
    </div>
    <div v-if="bankActionBlockedReason" class="bank-diagnostic">
      {{ bankActionBlockedReason }}
    </div>
    <div v-if="addBankUnavailableReason" class="bank-diagnostic">
      {{ addBankUnavailableReason }}
    </div>

    <!-- Empty State -->
    <div v-if="!BankList.length" class="bank-empty">
      <div class="bank-empty__icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="#3a3f50"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>
      </div>
      <p class="bank-empty__title">{{ $lang.bank_empty || 'No Bank Card' }}</p>
      <p class="bank-empty__desc">{{ $lang.bank_empty_desc || 'Add a bank card for withdrawals' }}</p>
      <button
        class="bank-add-btn"
        :disabled="bankReadError || bankActionBlockedReason || addBankUnavailableReason"
        @click="onClickRight"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
        <span>{{ $lang.bank_add || 'Add Bank Card' }}</span>
      </button>
    </div>

    <!-- Bank Card List -->
    <div v-else class="bank-list">
      <div
        v-for="(item, index) in BankList"
        :key="index"
        class="bank-card"
      >
        <div class="bank-card__top">
          <div class="bank-card__bank-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#ff5a1f"><path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/></svg>
          </div>
          <span class="bank-card__name">{{ item.bankName }}</span>
            <button
              class="bank-card__default"
              :class="{ 'bank-card__default--active': item.isDefault }"
              :disabled="bankActionBlockedReason || item.isDefault || settingDefaultCardId === item.cardId"
              @click="setDefaultBankCard(item)"
            >
            {{ item.isDefault ? ($lang.bank_default || 'Default') : (settingDefaultCardId === item.cardId ? ($lang.common_loading || 'Loading...') : ($lang.bank_set_default || 'Set default')) }}
          </button>
          <button
            class="bank-card__delete"
            :disabled="bankActionBlockedReason"
            @click="showDeletePopup(item.cardId)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          </button>
        </div>

        <div class="bank-card__number">
          <span v-if="item.isSee" class="bank-card__full">{{ item.bankCard }}</span>
          <span v-else class="bank-card__masked">{{ formatCardNumber(item.bankCard) }}</span>
          <div class="bank-card__actions">
            <button class="bank-card__action" @click="copyText(item.bankCard)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
            </button>
            <button class="bank-card__action" @click="toggleSee(item)">
              <svg v-if="item.isSee" width="18" height="18" viewBox="0 0 24 24" fill="#ff5a1f"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
            </button>
          </div>
        </div>

        <div class="bank-card__decoration">
          <div class="bank-card__circle bank-card__circle--1"></div>
          <div class="bank-card__circle bank-card__circle--2"></div>
        </div>
      </div>

      <!-- Add More Button -->
      <button
        class="bank-add-btn bank-add-btn--outline"
        :disabled="bankReadError || bankActionBlockedReason || addBankUnavailableReason"
        @click="onClickRight"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
        <span>{{ $lang.bank_add || 'Add Bank Card' }}</span>
      </button>
    </div>

    <!-- Delete Confirmation Popup -->
    <van-popup v-model="isShow" round :close-on-click-overlay="true" class="delete-popup">
      <div class="delete-popup__content">
        <div class="delete-popup__icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="#f5455c"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
        </div>
        <p class="delete-popup__title">{{ $lang.bank_txt || 'Remove this bank card?' }}</p>
        <div class="delete-popup__field">
          <label>{{ $lang.bank_txt2 || 'Withdrawal Password' }}</label>
          <div class="delete-popup__input" :class="{ 'delete-popup__input--focus': focus }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z"/></svg>
            <input
              v-model="privacyPwd"
              type="password"
              :placeholder="$lang.bank_pwd_ph || 'Enter withdrawal password'"
              @focus="focus = true"
              @blur="focus = false"
            />
          </div>
        </div>
        <div class="delete-popup__actions">
          <button class="delete-popup__btn delete-popup__btn--cancel" @click="isShow = false">
            {{ $lang.Cancelar || 'Cancel' }}
          </button>
          <button
            class="delete-popup__btn delete-popup__btn--confirm"
            :disabled="bankReadError || bankActionBlockedReason || addBankUnavailableReason"
            @click="Confirm(deleteCardId)"
          >
            {{ $lang.Confirmar || 'Confirm' }}
          </button>
        </div>
      </div>
    </van-popup>

    <!-- Add Bank Card Popup -->
    <van-popup v-model="showAddPopup" round :close-on-click-overlay="true" class="add-popup">
      <div class="add-popup__content">
        <h3 class="add-popup__title">{{ $lang.BankInfo_txt13 || 'Add Bank Card' }}</h3>

        <!-- Real Name -->
        <div class="add-popup__field">
          <label>{{ $lang.common_txt46 || 'Real Name' }}</label>
          <div class="add-popup__input">
            <input
              v-model="addRealName"
              :readonly="addRealNameReadonly"
              :placeholder="$lang.common_txt46 || 'Enter real name'"
            />
          </div>
        </div>

        <!-- Bank Name Selector -->
        <div class="add-popup__field">
          <label>{{ $lang.bank_title || 'Bank' }}</label>
          <div class="add-popup__input add-popup__input--select" @click="toggleBankSelect">
            <span :class="{ 'add-popup__placeholder': !addBankName }">{{ addBankName || 'Select bank' }}</span>
            <van-icon :name="showBankSelect ? 'arrow-up' : 'arrow-down'" size="14" color="var(--vk-t2)" />
          </div>
          <div v-if="showBankSelect" class="add-popup__dropdown">
            <div
              v-for="(item, idx) in addBankColumns"
              :key="idx"
              class="add-popup__dropdown-item"
              :class="{ 'add-popup__dropdown-item--active': idx === addSelectIndex }"
              @click="selectBank(idx, item)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- Bank Card Number -->
        <div class="add-popup__field">
          <label>{{ ['Otbet','Kkbet'].includes(GET_PLATFORM) ? ($lang.common_txt47 || 'Bank Card Number') : ($lang.common_txt7 || 'Account Number') }}</label>
          <div class="add-popup__input">
            <span v-if="!['Otbet','Kkbet'].includes(GET_PLATFORM)" class="add-popup__prefix">{{ GET_AREACODE }}</span>
            <input
              v-model="addBankCard"
              :placeholder="['Otbet','Kkbet'].includes(GET_PLATFORM) ? ($lang.common_txt47 || 'Enter bank card number') : ($lang.common_txt7 || 'Enter account number')"
              :maxlength="['Otbet','Kkbet'].includes(GET_PLATFORM) ? 100 : 11"
            />
          </div>
        </div>

        <!-- Actions -->
        <div class="add-popup__actions">
          <button class="add-popup__btn add-popup__btn--cancel" @click="showAddPopup = false">
            {{ $lang.Cancelar || 'Cancel' }}
          </button>
          <button
            class="add-popup__btn add-popup__btn--confirm"
            :disabled="addSubmitting || bankReadError || bankActionBlockedReason || addBankUnavailableReason"
            @click="submitAddBank"
          >
            {{ addSubmitting ? ($lang.common_loading || 'Loading...') : ($lang.Confirmar || 'Confirm') }}
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { BankCardInfo, DelBankCard, AddBankCardInit, AddBankCard, SetDefaultBankCard } from '@/api/common'
import md5 from 'md5'
import { contentOf, failMessage, firstArray, getAddBankSubmitBlockReason, handleNeedLogin, isOk, isReadOnlyQuerySuccess, isSafeWriteSuccess, normalizeAddBankColumns, toArray } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'
import { copyTextToClipboard } from '@/components/share/shareSafety'

const BANK_WRITE_BLOCKED_MESSAGE = 'This feature is temporarily unavailable. Please try again later.'
const BANK_WRITE_SUCCESS_COPY_TOKENS = ['ok', 'success', 'successful', 'succeed', 'succeeded', 'done', 'saved', 'complete', 'completed', 'sucesso', 'exito', 'éxito', '成功']

function safeToast(vm, payload) {
  if (vm && typeof vm.$toast === 'function') {
    vm.$toast(payload)
  }
}

function getLocalStorage() {
  try {
    return typeof safeLocalStorage === 'function' ? safeLocalStorage() : null
  } catch (e) {
    return null
  }
}

function bankWriteMessageToken(value) {
  return String(value === undefined || value === null ? '' : value).trim().toLowerCase().replace(/[.!。！]+$/g, '')
}

const MINE_BLOCKED_MARKER_KEYS = ['failClosed', 'fail_closed', 'falseProof', 'false_proof', 'readOnlyAction', 'writeBlocked', 'backendBlocked', 'backendFailure', 'backend_failure', 'technicalMarker', 'technical_marker', 'mock', 'fallback']
const MINE_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']
const BANK_STRICT_WRITE_PROOF_KEYS = ['writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof', 'affectedRowsProof', 'affected_rows_proof']
const BANK_STRICT_WRITE_COUNT_KEYS = ['affected', 'affectedRows', 'affected_rows', 'rowCount', 'row_count']
const BANK_STRICT_FALSE_PROOF_TOKENS = ['0', 'false', 'true', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder']
const BANK_STRICT_BLOCKED_PROOF_TOKENS = ['stub', 'mock', 'fallback', 'placeholder', 'read_only', 'readonly', 'query_only', 'only_query', 'unavailable', 'unsupported', 'failclosed', 'fail_closed', 'writeblocked', 'write_blocked', 'backendblocked', 'backend_blocked', 'backendfailure', 'backend_failure', 'technicalmarker', 'technical_marker', 'falseproof', 'false_proof']

function hasMineBlockedWriteMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasMineBlockedWriteMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const token = bankWriteMessageToken(value)
    if (MINE_BLOCKED_MARKER_KEYS.includes(key) && value !== undefined && value !== null && value !== '') return true
    if (MINE_FALSE_PROOF_KEYS.includes(key) && (value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token))) return true
    if (token === 'placeholder') return true
    return value && typeof value === 'object' && hasMineBlockedWriteMarker(value, depth + 1, seen)
  })
}

function bankProofToken(value) {
  return bankWriteMessageToken(value).replace(/[\s-]+/g, '_')
}

function hasBankPositiveWriteCount(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const token = bankProofToken(value)
  return /^\d+$/.test(token) && !/^0+$/.test(token)
}

function hasBankStrictProofValue(value) {
  if (Array.isArray(value) || (value && typeof value === 'object')) return false
  if (typeof value === 'boolean') return false
  const token = bankProofToken(value)
  return !!token &&
    !BANK_STRICT_FALSE_PROOF_TOKENS.includes(token) &&
    !BANK_STRICT_BLOCKED_PROOF_TOKENS.some(blocked => token.includes(blocked)) &&
    !token.includes('fail') &&
    !token.includes('error')
}

function hasBankStrictWriteProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasBankStrictWriteProof(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (BANK_STRICT_WRITE_COUNT_KEYS.includes(key)) return hasBankPositiveWriteCount(value)
    if (BANK_STRICT_WRITE_PROOF_KEYS.includes(key)) return hasBankStrictProofValue(value)
    return value && typeof value === 'object' && hasBankStrictWriteProof(value, depth + 1, seen)
  })
}

function isMineSafeWriteSuccess(res) {
  return isSafeWriteSuccess(res) && hasBankStrictWriteProof(res) && !hasMineBlockedWriteMarker(res)
}

export default {
  name: 'Bank',
  data() {
    return {
      BankList: [],
      isShow: false,
      focus: false,
      privacyPwd: '',
      deleteCardId: null,
      // Add bank card popup
      showAddPopup: false,
      addRealName: '',
      addRealNameReadonly: false,
      addBankName: '',
      addBankId: null,
      addBankCard: '',
      addBankColumns: [],
      showBankSelect: false,
      addSelectIndex: 0,
      addSubmitting: false,
      settingDefaultCardId: null,
      bankReadError: '',
      bankActionBlockedReason: '',
      addBankUnavailableReason: ''
    }
  },
  mounted() {
    if (!this.ensureLogin()) return
    this.BankCardInfo()
  },
  methods: {
    ensureLogin() {
      if (safeGetStorageItem(getLocalStorage(), 'token')) return true
      this.requireLogin()
      return false
    },
    hasBankActionBlock() {
      const message = this.bankReadError || this.bankActionBlockedReason || this.addBankUnavailableReason
      if (!message) return false
      safeToast(this, { message, icon: 'cross' })
      return true
    },
    requireLogin() {
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$jumpTo('/home', {}, { replace: true })
    },
    bankWriteFailMessage(data, fallbackMessage) {
      const message = fallbackMessage || failMessage(data, BANK_WRITE_BLOCKED_MESSAGE)
      const token = bankWriteMessageToken(message)
      const localSuccessToken = bankWriteMessageToken(this.$lang && this.$lang.Sucesso)
      if (BANK_WRITE_SUCCESS_COPY_TOKENS.includes(token) || (localSuccessToken && token === localSuccessToken)) {
        return BANK_WRITE_BLOCKED_MESSAGE
      }
      return message || BANK_WRITE_BLOCKED_MESSAGE
    },
    formatCardNumber(cardNumber) {
      cardNumber = String(cardNumber || '')
      if (cardNumber.length <= 4) return cardNumber
      return '**** **** ' + cardNumber.slice(-4)
    },
    toggleSee(item) {
      this.$set(item, 'isSee', !item.isSee)
    },
    isDefaultCard(item) {
      const value = item && (item.isDefault !== undefined ? item.isDefault
        : item.is_default !== undefined ? item.is_default
          : item.defaultFlag !== undefined ? item.defaultFlag
            : item.default_flag)
      const token = String(value === undefined || value === null ? '' : value).trim().toLowerCase()
      return value === true || value === 1 || ['true', '1', 'yes', 'default'].includes(token)
    },
    copyText(v) {
      if (!v) {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      let copied = false
      try {
        copied = copyTextToClipboard(v)
      } catch (e) {
        copied = false
      }
      safeToast(this, {
        message: copied ? (this.$lang.Sucesso || 'Success') : (this.$lang.copy_failed || 'Copy failed'),
        icon: copied ? 'success' : 'cross'
      })
    },
    showDeletePopup(cardId) {
      if (!this.ensureLogin()) return
      if (this.hasBankActionBlock()) return
      if (cardId === null || cardId === undefined || cardId === '') {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      this.deleteCardId = cardId
      this.privacyPwd = ''
      this.isShow = true
    },
    async Confirm(cardId) {
      if (!this.ensureLogin()) return
      if (this.hasBankActionBlock()) return
      if (cardId === null || cardId === undefined || cardId === '') {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      if (!this.privacyPwd) {
        safeToast(this, { message: this.$lang.bank_txt2 || 'Enter Withdrawal Password', icon: 'cross' })
        return
      }
      try {
        const data = await DelBankCard({
          privacyPwd: md5(this.privacyPwd),
          cardId: cardId
        })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.bankActionBlockedReason = ''
          this.isShow = false
          await this.BankCardInfo()
          safeToast(this, { message: this.$lang.Sucesso, icon: 'success' })
        } else {
          const rawFailureMessage = failMessage(data)
          const backendFailureMessage = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.bankActionBlockedReason = this.bankWriteFailMessage(data, backendFailureMessage)
          if (this.bankActionBlockedReason === rawFailureMessage) {
            safeToast(this, { message: failMessage(data), icon: 'cross' })
          } else {
            safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
          }
        }
      } catch (e) {
        this.bankActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
      }
    },
    async setDefaultBankCard(item) {
      if (!this.ensureLogin()) return
      if (this.hasBankActionBlock()) return
      if (!item) {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      if (item.isDefault) {
        return
      }
      const cardId = item.cardId
      if (cardId === null || cardId === undefined || cardId === '') {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      if (this.settingDefaultCardId === cardId) {
        return
      }
      this.settingDefaultCardId = cardId
      try {
        const data = await SetDefaultBankCard({ cardId })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data)) {
          if (isMineSafeWriteSuccess(data)) {
            this.bankActionBlockedReason = ''
            await this.BankCardInfo()
            safeToast(this, { message: this.$lang.Sucesso || 'Success', icon: 'success' })
          } else {
            this.bankActionBlockedReason = this.bankWriteFailMessage(data, BANK_WRITE_BLOCKED_MESSAGE)
            safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
          }
        } else {
          const rawFailureMessage = failMessage(data)
          const backendFailureMessage = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.bankActionBlockedReason = this.bankWriteFailMessage(data, backendFailureMessage)
          if (this.bankActionBlockedReason === rawFailureMessage) {
            safeToast(this, { message: failMessage(data), icon: 'cross' })
          } else {
            safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
          }
        }
      } catch (e) {
        this.bankActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
      } finally {
        this.settingDefaultCardId = null
      }
    },
    onClickRight() {
      if (!this.ensureLogin()) return
      if (this.hasBankActionBlock()) return
      this.showAddPopup = true
      this.addBankCard = ''
      this.fetchAddBankCardInit()
    },
    async fetchAddBankCardInit() {
      if (!this.ensureLogin()) return
      try {
        const data = await AddBankCardInit()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        if (isOk(data)) {
          this.addBankUnavailableReason = ''
          const content = contentOf(data)
          const realName = content.realName || content.real_name || content.name || ''
          if (realName) {
            this.addRealName = realName
            this.addRealNameReadonly = true
          } else {
            this.addRealName = ''
            this.addRealNameReadonly = false
          }
          const bankList = firstArray(content, ['bankList', 'bank_list', 'banks', 'items', 'list', 'records', 'dataList'])
          this.addBankColumns = normalizeAddBankColumns(bankList)
          if (this.addBankColumns.length > 0) {
            this.addBankId = this.addBankColumns[0].value
            this.addBankName = this.addBankColumns[0].label
            this.addSelectIndex = 0
          } else {
            this.addBankId = null
            this.addBankName = ''
            this.addSelectIndex = 0
          }
        } else {
          this.addBankUnavailableReason = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.addBankUnavailableReason, icon: 'cross' })
        }
      } catch (e) {
        this.addBankUnavailableReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.addBankUnavailableReason, icon: 'cross' })
      }
    },
    toggleBankSelect() {
      if (this.addBankColumns.length === 0) {
        safeToast(this, { message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      this.showBankSelect = !this.showBankSelect
    },
    selectBank(index, item) {
      if (!item) return
      this.addBankId = item.value
      this.addBankName = item.label
      this.addSelectIndex = index
      this.showBankSelect = false
    },
    async submitAddBank() {
      if (!this.ensureLogin()) return
      if (this.hasBankActionBlock()) return
      const bankCard = String(this.addBankCard || '').trim()
      const realName = String(this.addRealName || '').trim()
      if (!realName) {
        safeToast(this, { message: this.$lang.common_txt46 || 'Enter real name', icon: 'cross' })
        return
      }
      if (!bankCard) {
        safeToast(this, { message: this.$lang.BankInfo_txt16 || 'Please enter bank card number', icon: 'cross' })
        return
      }
      const bankBlockReason = getAddBankSubmitBlockReason({
        addBankColumns: this.addBankColumns,
        addBankId: this.addBankId,
        addBankName: this.addBankName
      })
      if (bankBlockReason) {
        const message = bankBlockReason === 'BANK_LIST_UNAVAILABLE'
          ? (this.$lang.bank_list_unavailable || 'This feature is temporarily unavailable. Please try again later.')
          : (this.$lang.bank_title || 'Select bank')
        safeToast(this, { message, icon: 'cross' })
        return
      }
      // 非非洲银行卡布局（Otbet/Kkbet）才走 8-11 位短卡号校验
      if (!['Otbet', 'Kkbet'].includes(this.GET_PLATFORM)) {
        const reg = /^[1-9]\d{7,10}$/
        if (!reg.test(bankCard)) {
          safeToast(this, { message: this.$lang.common_txt376 || 'Invalid card number', icon: 'cross' })
          return
        }
      } else if (bankCard.length < 4 || bankCard.length > 100) {
        safeToast(this, { message: this.$lang.common_txt376 || 'Invalid card number', icon: 'cross' })
        return
      }
      this.addSubmitting = true
      try {
        const data = await AddBankCard({
          bankName: this.addBankName,
          bankId: this.addBankId,
          bankCard,
          realName
        })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.bankActionBlockedReason = ''
          this.addBankUnavailableReason = ''
          this.showAddPopup = false
          safeToast(this, { message: this.$lang.Sucesso || 'Success', icon: 'success' })
          await this.BankCardInfo()
        } else {
          const backendFailureMessage = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.bankActionBlockedReason = this.bankWriteFailMessage(data, backendFailureMessage)
          safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.bankActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.bankActionBlockedReason, icon: 'cross' })
      } finally {
        this.addSubmitting = false
      }
    },
    async BankCardInfo() {
      if (!this.ensureLogin()) return
      const $toast = payload => safeToast(this, payload)
      try {
        const data = await BankCardInfo()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        const isReadOnlyQuery = typeof isReadOnlyQuerySuccess === 'function' && isReadOnlyQuerySuccess(data)
        if (isOk(data) || isReadOnlyQuery) {
          this.bankReadError = ''
          if (isReadOnlyQuery) {
            this.bankActionBlockedReason = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          }
          const content = contentOf(data)
          const bankCardList = firstArray(content, ['bankCardList', 'list', 'records', 'dataList'])
          this.BankList = toArray(bankCardList).map(raw => {
            const item = raw || {}
            const cardId = [item.cardId, item.bankCardId, item.id, item.card_id]
              .find(value => value !== null && value !== undefined && value !== '')
            return {
              ...item,
              cardId,
              defaultValue: item.isDefault !== undefined ? item.isDefault : item.is_default !== undefined ? item.is_default : item.defaultFlag !== undefined ? item.defaultFlag : item.default_flag,
              isDefault: this.isDefaultCard(item),
              bankName: item.bankName || item.bank_name || item.bank || item.name || '',
              bankCard: item.bankCard || item.bank_card || item.accountNo || item.account_no || item.cardNumber || item.card_number || item.cardNo || item.card_no || '',
              isSee: false
            }
          })
        } else {
          this.bankReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          $toast({ message: this.bankReadError, icon: 'cross' })
        }
      } catch (e) {
        this.bankReadError = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        $toast({ message: this.bankReadError, icon: 'cross' })
      }
    }
  }
}
</script>

<style lang="less" scoped>
@bg: @vk-bg;       /* 页面底 */
@card: @vk-surface;/* 卡片面 */
@neon: @brand;     /* 绿→火焰橙(强调/激活) */
@muted: @vk-t2;    /* 次文字 */
@border: rgba(255, 255, 255, 0.06);

.bank-page {
  min-height: 100vh;
  background: @bg;
  padding-bottom: 40px;
}

// ====== EMPTY STATE ======
.bank-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;

  &__icon {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
  }

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 8px;
  }

  &__desc {
    font-size: var(--vk-fs-sm);
    color: @muted;
    margin: 0 0 32px;
  }
}

// ====== BANK CARD ======
.bank-list {
  padding: 12px 16px;
}

.bank-card {
  background: linear-gradient(135deg, #2a3142 0%, #1e2535 50%, #252936 100%);
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 14px;
  border: 1px solid @border;
  position: relative;
  overflow: hidden;

  &__top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }

  &__bank-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(255, 90, 31, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    flex: 1;
    font-size: var(--vk-fs-15);
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
  }

  &__delete {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    transition: background 0.15s;

    &:active {
      background: rgba(239, 68, 68, 0.1);
    }
  }

  &__default {
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    max-width: 112px;
    min-height: 28px;
    padding: 5px 9px;
    border: 1px solid rgba(255, 90, 31, 0.38);
    border-radius: 999px;
    background: rgba(255, 90, 31, 0.08);
    color: @neon;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    line-height: 16px;
    text-transform: uppercase;
    cursor: pointer;

    &--active,
    &:disabled {
      cursor: default;
      color: @bg;
      background: @neon;
      border-color: @neon;
    }
  }

  &__number {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__full,
  &__masked {
    font-size: var(--vk-fs-lg);
    font-weight: 600;
    color: #ffffff;
    letter-spacing: 2px;
    font-family: 'Courier New', monospace;
  }

  &__masked {
    color: @muted;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__action {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:active {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  // Decorative circles
  &__decoration {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  &__circle {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.04);

    &--1 {
      width: 120px;
      height: 120px;
      top: -30px;
      right: -20px;
    }

    &--2 {
      width: 80px;
      height: 80px;
      bottom: -20px;
      right: 40px;
    }
  }
}

// ====== ADD BUTTON ======
.bank-add-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: @neon;
  color: @bg;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: var(--vk-fs-base);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 90, 31, 0.25);
  transition: transform 0.1s;

  &:active {
    transform: scale(0.98);
  }

  &--outline {
    background: transparent;
    color: @neon;
    border: 1px dashed rgba(255, 90, 31, 0.3);
    box-shadow: none;
    margin-top: 8px;
  }
}

// ====== DELETE POPUP ======
.delete-popup {
  background: @card !important;
  border-radius: 16px !important;

  &__content {
    padding: 28px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__icon {
    margin-bottom: 16px;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin: 0 0 20px;
  }

  &__field {
    width: 100%;
    margin-bottom: 24px;

    label {
      display: block;
      font-size: var(--vk-fs-xs);
      color: @muted;
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px 14px;
    transition: border-color 0.2s;

    &--focus {
      border-color: @neon;
      box-shadow: 0 0 0 2px rgba(255, 90, 31, 0.1);
    }

    input {
      flex: 1;
      background: transparent;
      border: none;
      color: #ffffff;
      font-size: var(--vk-fs-base);
      outline: none;

      &::placeholder {
        color: #4b5563;
      }
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    font-size: var(--vk-fs-base);
    font-weight: 700;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: transform 0.1s;

    &:active {
      transform: scale(0.98);
    }

    &--cancel {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }

    &--confirm {
      background: var(--vk-danger);
      color: #ffffff;
    }
  }
}

// ====== ADD POPUP ======
.add-popup {
  background: @card !important;
  border-radius: 16px !important;
  width: 90%;
  max-width: 400px;

  &__content {
    padding: 24px 20px;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin: 0 0 20px;
  }

  &__field {
    margin-bottom: 16px;
    position: relative;

    label {
      display: block;
      font-size: var(--vk-fs-xs);
      color: @muted;
      margin-bottom: 6px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px 14px;

    input {
      flex: 1;
      background: transparent;
      border: none;
      color: #ffffff;
      font-size: var(--vk-fs-base);
      outline: none;

      &::placeholder {
        color: #4b5563;
      }

      &[readonly] {
        color: @muted;
      }
    }

    &--select {
      cursor: pointer;
      justify-content: space-between;

      span {
        font-size: var(--vk-fs-base);
        color: #ffffff;
      }
    }
  }

  &__prefix {
    color: @neon;
    font-weight: 600;
    font-size: var(--vk-fs-sm);
    flex-shrink: 0;
  }

  &__placeholder {
    color: #4b5563 !important;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #2a2f3e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    max-height: 160px;
    overflow-y: auto;
    z-index: 10;
    margin-top: 4px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }

  &__dropdown-item {
    padding: 10px 14px;
    font-size: var(--vk-fs-sm);
    color: #ffffff;
    cursor: pointer;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:last-child { border-bottom: none; }
    &:active { background: rgba(255, 90, 31, 0.1); }

    &--active {
      background: rgba(255, 90, 31, 0.15);
      color: @neon;
      font-weight: 600;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  &__btn {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    font-size: var(--vk-fs-base);
    font-weight: 700;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: transform 0.1s;

    &:active { transform: scale(0.98); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }

    &--cancel {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }

    &--confirm {
      background: @neon;
      color: @bg;
    }
  }
}

@media (min-width: 769px) {
  .bank-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
